import React, { createContext, useState, useCallback, useEffect, useRef } from "react";
import MemberService from "../services/member_service";
import { useTenant } from "../hooks/useTenant";
import { memberDataFromFirestore } from "../models/member";

/**
 * MembersContext
 *
 * Cache centralizado de membros para evitar múltiplas leituras Firestore.
 * Qualquer componente que precise da lista de membros (lista, busca global, etc.)
 * deve usar `useMembers()` em vez de chamar MemberService diretamente.
 *
 * Economia estimada: ~6.000 reads/sessão (de 13 chamadas → 1 chamada).
 */
export const MembersContext = createContext({
    members: [],
    loading: true,
    refresh: async () => {},
    lastFetchedAt: null,
    addOrUpdateMember: () => {},
    removeMember: () => {},
});

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos — recarrega se stale

export const MembersProvider = ({ children }) => {
    const { idTenant, loading: tenantLoading } = useTenant();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const lastFetchedAtRef = useRef(null);
    const fetchingRef = useRef(false);

    const fetchMembers = useCallback(async (force = false) => {
        if (!idTenant) return;
        if (fetchingRef.current) return; // evitar chamadas paralelas

        // Se não forçar e o cache ainda é válido, não buscar novamente
        if (!force && lastFetchedAtRef.current) {
            const age = Date.now() - lastFetchedAtRef.current;
            if (age < CACHE_TTL_MS) return;
        }

        fetchingRef.current = true;
        setLoading(true);

        try {
            const data = await MemberService.getMembersByStatus(idTenant, "all");
            setMembers(data || []);
            lastFetchedAtRef.current = Date.now();
        } catch (error) {
            console.error("[MembersProvider] Erro ao carregar membros:", error);
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, [idTenant]);

    // Carrega ao montar (quando tenant estiver pronto)
    useEffect(() => {
        if (!tenantLoading && idTenant) {
            fetchMembers();
        }
    }, [tenantLoading, idTenant, fetchMembers]);

    const addOrUpdateMember = useCallback((rawMember) => {
        if (!rawMember) return;

        // Se o membro estiver no formato do Firestore (address é um objeto),
        // converte para o formato flat do formulário/cache.
        const isFirestoreFormat = rawMember.address && typeof rawMember.address === 'object';
        const flatMember = isFirestoreFormat 
            ? memberDataFromFirestore(rawMember.id, rawMember)
            : rawMember;

        setMembers(prev => {
            const index = prev.findIndex(m => m.id === flatMember.id);
            let updated;
            if (index > -1) {
                updated = [...prev];
                updated[index] = { ...updated[index], ...flatMember };
            } else {
                updated = [...prev, flatMember];
            }
            return updated.sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
        });
    }, []);

    const removeMember = useCallback((memberId) => {
        if (!memberId) return;
        setMembers(prev => prev.filter(m => m.id !== memberId));
    }, []);

    const value = {
        members,
        loading,
        refresh: () => fetchMembers(true), // force = true ignora TTL
        lastFetchedAt: lastFetchedAtRef.current,
        addOrUpdateMember,
        removeMember,
    };

    return (
        <MembersContext.Provider value={value}>
            {children}
        </MembersContext.Provider>
    );
};
